import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Spinner,
  getKeyValue,
  SelectItem,
  Select,
  Button,
  Input,
  ModalBody,
  ModalFooter,
  Checkbox,
  Divider,
  Skeleton,
  useDisclosure,
} from "@heroui/react";
import { FaArrowRight } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { CloseIcon, CreateIcon, SearchIcon, TableEditIcon } from "../../utils/svgIcons";
import ModalBox from "../PopupModals/Modal";
import CreateCombinations from "../../pages/BrandCombination/CreateCombinations";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { BrandCombinationIcon, SourceIcon, SubSourceIcon } from "../../utils/svgIcons";
import { useDispatch } from "react-redux";
import TableData from "./TableData";
import DynamicPopover from "../DynamicPopover/DynamicPopover";
import useDebounce from "../../utils/useDebounce";
import { useAuth } from "../../hooks/useAuth";
import { Crypt } from 'hybrid-crypto-js';


export const pageLimitsNumber = [
  { value: "10", label: "10", key: "10", },
  { value: "20", label: "20", key: "20" },
  { value: "50", label: "50", key: "50" },
  { value: "100", label: "100", key: "100" },
  { value: "150", label: "150", key: "150" },
];

const formatDate = (isoString) => {
  if (!isoString) return "-";

  const date = new Date(isoString);

  // Format date manually as DD/MM/YYYY
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;

  // Format time using toLocaleTimeString
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  return `${formattedDate}, ${formattedTime}`;
};


export const isValidEmailAddress = (emailAddress) => {
  emailAddress = emailAddress.trim();
  var emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(emailAddress);
};

// Get Validation Schema 
const getValidationSchema = (pageType, isEdit) => {
  switch (pageType) {
    case "BrandCombination":
    case "Brand Combination":
      return yup.object().shape({
        source: yup.string().required("Source field is required!"),
        subSource: yup.string().required("Sub Source field is required!"),
        brand: yup.string().required("Brand Name field is required!"),
      });

    case "SourceCombination":
    case "Source Combination":
      return yup.object().shape({
        name: yup.string().required("Source Name field is required!"),
        sourceNumber: yup
          .number()
          .typeError("Source Number is required")
          .required("Source Number field is required!"),
      });

    case "SubSourceCombination":
    case "Sub Source Combination":
      return yup.object().shape({
        name: yup.string().required("Sub Source Name field is required!"),
        subSourceNumber: yup
          .number()
          .typeError("Sub Source Number is required")
          .required("Sub Source field is required!"),
      });

    case "TpLeads":
      return yup.object().shape({
        leads: yup.array().of(
          yup.object().shape({
            name: yup.string().required("Name is required"),
            mobile: yup
              .string()
              .required("Mobile Number is required")
              .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
            pincode: yup
              .string()
              .when("services", {
                is: (services) => {
                  return services && (
                    services.TVS === true ||
                    services.AMPERE === true
                  );
                },
                then: (schema) => schema
                  .required("Pincode is required for selected service(s)"),
                otherwise: (schema) => schema.optional(),
              }),
            // .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
            services: yup
              .object()
              .required("At least one service is required")
              .test(
                "has-service",
                "At least one service must be selected",
                (value) => {
                  return value && Object.values(value).some((v) => v === true);
                }
              ),
          })
        ),
      });

    case "TPUsers":
    case "TpUsers":
      return yup.object().shape({
        name: yup.string().required("Name field is required!"),
        email: yup
          .string()
          .required("Email field is required!")
          .test(
            "is-valid-email",
            "Please enter a valid email address!",
            (value) => isValidEmailAddress(value)
          ),
        password: isEdit
          ? yup.string()
          : yup.string()
            .required("Password field is required!")
            .min(6, "Password must be at least 6 characters long.")
            .matches(/[a-z]/, "Password must contain at least one lowercase letter.")
            .matches(/[A-Z]/, "Password must contain at least one uppercase letter.")
            .matches(/[0-9]/, "Password must contain at least one number.")
            .matches(
              /[@$!%*?&]/,
              "Password must contain at least one special character (@, $, !, %, *, ?, &)."
            ),
        roleId: yup.string().required("Role field is required!"),
      });
    default:
      return yup.object();
  }
};



export default function DefaultTableLayout({ pageType, columnType, allLeadsData, loading, getAllLeads, fetchColumns, allCombinationData, createCombination, getSpecificCombination, editCombination, deleteCombination, isSearchable, getAllSubSourceList, getAllSourceList, isleadAction = true, columnsData, columnLoading, columnsError, getRolesList, getCompanyList, getSubAdminDetailsById, savePreference, fetchPreferences, deletePreference }) {
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();

  // Pagination hooks
  const [pageLimit, setPageLimit] = useState('10');
  const [page, setPage] = React.useState(1);

  // Open Modal
  const [isCombinationModalOpen, setIsCombinationModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isDeleteCombinationModalOpen, setIsDeleteCombinationModalOpen] = useState(false);
  const [isDeleteModalLoading, setIsDeleteModalLoading] = useState(false);

  // API Res.. Loading 
  const [searchInput, setSearchInput] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [selectSource, setSelectSource] = useState([]);
  const [selectSubSource, setSelectSubSource] = useState([]);
  const [searchApplied, setSearchApplied] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState('');

  // Edit Combination
  const [editId, setEditId] = useState(null);  // store specific id for edit
  const [isEdit, setIsEdit] = useState(false); // check it's edit or create

  // Delete Combination
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [deleteData, setDeleteData] = useState({});

  // Dynamic Columns hooks
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Role Option hooks
  const [roleOption, setRoleOption] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");

  // Company option Hooks
  const [companyoption, setCompanyOption] = useState([])
  const [isCompanyoptionLoaded, setIsCompanyoptionLoaded] = useState(false);


  // Form Validation
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: pageType === "TpLeads"
      ? {
        leads: [
          {
            name: "",
            mobile: "",
            pincode: "",
            services: {
              OLA: true,
              TVS: false,
              REVOLT: false,
              AMPERE: false,
              SIMPLE: false
            },
            admin: {
              id: user?._id,
              type: user?.type,
              source: 16,
            },
          },
        ],
      }
      : {},
    resolver: yupResolver(getValidationSchema(pageType, isEdit)),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "leads"
  });

  // Search by Mobile No.
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  }

  const searchFunctions = () => {
    if (searchInput.trim() === '') {
      if (searchApplied) {
        clearSearch();
      }
      return;
    }

    setSearchApplied(true); // mark search as active
    setAppliedSearch(searchInput.trim());
    let apiParams = pageType === "TpLeads" ? { page: page, limit: pageLimit, search: searchInput.trim(), company: 'TP', admin: { id: user?._id, type: user?.type } } : { page: page, limit: pageLimit, search: searchInput.trim() };
    getAllLeads(apiParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchApplied(false);
    setAppliedSearch('');

    let apiParams = pageType === "TpLeads" ? { page: page, limit: pageLimit, company: 'TP', admin: { id: user?._id, type: user?.type } } : { page: page, limit: pageLimit, search: '' };
    getAllLeads(apiParams);
  };

  // const handleSearchInputChange = useCallback((e) => {
  //   const value = e.target.value || '';
  //   setSearchInput(value);
  //   if (value?.length > 1) {
  //     setShowOptions(true);
  //   } else {
  //     setShowOptions(false);
  //   }
  // }, []);

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 500);
  let apiParams = pageType === "TpLeads" ? { page: page, limit: pageLimit, search: debouncedSearch, company: 'TP', admin: { id: user?._id, type: user?.type } } : { page: page, limit: pageLimit, search: debouncedSearch };
  useEffect(() => {
    const fetchLeadData = async () => {
      setIsLoading(true);
      try {
        await getAllLeads(apiParams).then((res) => {
          setIsLoading(false);
        }).catch((err) => {
          setIsLoading(false);
          toast.error(err.response.data.message);
        });
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching leads:', error);
      } finally {
        setIsLoading(false);
        setShowOptions(false);
      }
    }
    fetchLeadData();
    dispatch(fetchColumns(columnType));


    getAllSourceList({ limit: 1000 })?.then((res) => {
      const result = res.data.result.docs;
      const sourceOptions = result.map((data) => {
        return { label: data.name, value: data.sourceNumber };
      });
      setSelectSource(sourceOptions);
    });

    getAllSubSourceList({ limit: 1000 })?.then((res) => {
      const result = res.data.result.docs;
      const subSourceOptions = result.map((data) => {
        return { label: data.name, value: data.subSourceNumber };
      });
      setSelectSubSource(subSourceOptions);
    });

    getRolesList(1, 1000, "")?.then((res) => {
      let roleslist = res.data.result;
      let newData = roleslist.docs.map((s) => ({
        value: s._id,
        label: s.name,
      }));
      setRoleOption(newData);
    });

    if (selectedRole[0] === '6610f0fbd22a12c26454baf4') {
      setIsCompanyoptionLoaded(true);
      getCompanyList()?.then((res) => {
        let data = res.data.result;
        let newData = data.map((s) => ({
          value: s._id,
          label: s.title,
        }));
        setTimeout(() => {
          setIsCompanyoptionLoaded(false);
          setCompanyOption(newData);
        }, 1000);
      })
        .catch((err) => {
          setIsCompanyoptionLoaded(false);
          toast.error(err?.response?.data?.message || "Something went wrong");
        });
    }
  }, [dispatch, selectedRole]);


  // Handle Dynamic Show default columns
  useEffect(() => {
    if (pageType && (selectedColumns && selectedColumns.length > 0)) {
      const savedColumns = JSON.parse(localStorage.getItem('savedColumns')) || {};
      savedColumns[pageType] = selectedColumns;
      localStorage.setItem('savedColumns', JSON.stringify(savedColumns));
    } else if (columnsData && columnsData.length > 0) {
      const data = columnsData.filter(col => col?.default).map(col => col?.uid);
      setSelectedColumns(data);
    }
  }, [columnsData]);

  // Toggle individual selection
  const toggleItem = (uid) => {
    setSelectedColumns((prev) =>
      prev.includes(uid) ? prev.filter((item) => item !== uid) : [...prev, uid]
    );
  };

  let changedTitle = pageType.replace(/([a-z])([A-Z])/g, '$1 $2');

  function formatNumberValue(number) {
    if (typeof number !== 'number') {
      return 'Invalid input'; // Handle non-numeric inputs
    }
    return new Intl.NumberFormat('en-IN').format(number);
  }

  // Combination Modal toggle
  const toggleCombinationModal = () => {
    setIsCombinationModalOpen(true);
  };

  // Create Combination
  const handlehandleConfirm = async (data) => {
    setIsModalLoading(true);
    try {
      let apiCall;

      const getPayload = (data) => {
        return Object.keys(data).reduce((acc, key) => {
          const value = data[key];

          // Skip if value is "" (empty string), [] (empty array), or {} (empty object)
          if (
            value === "" ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0)
          ) {
            return acc;
          }

          if (key === "leads") {
            const updatedLeads = value.map(lead => ({
              ...lead,
              admin: {
                id: user?._id,
                type: user?.type,
                source: 16,
              },
            }));
            acc[key] = JSON.stringify(updatedLeads); // leads as JSON string
            // acc[key] = JSON.stringify(value); // leads as JSON string
          } else if (key === "password") {
            let crypt = new Crypt();
            acc[key] = crypt.encrypt(process.env.REACT_APP_ENCRYPT_KEY, value);
          } else if (
            key === "brand" ||
            key === "name" ||
            key === "mobile" ||
            key === "email" ||
            key === "roleId" ||
            key === "companyId" ||
            key === "pincode"
          ) {
            acc[key] = value;
          } else {
            acc[key] = Number(value);
          }

          return acc;
        }, {});
      };

      const payload = getPayload(data);

      if (isEdit && originalData) {
        switch (pageType) {
          case "BrandCombination":
          case "Brand Combination":
            if (
              payload.brand === originalData.brand &&
              payload.source === originalData.source &&
              payload.subSource === originalData.subSource
            ) {
              toast.error("No changes detected. Please update at least one field before submitting.");
              setIsModalLoading(false);
              return;
            }
            break;

          case "SourceCombination":
          case "Source Combination":
            if (
              payload.name === originalData.name &&
              payload.sourceNumber === originalData.sourceNumber
            ) {
              toast.error("No changes detected. Please update at least one field before submitting.");
              setIsModalLoading(false);
              return;
            }
            break;

          case "SubSourceCombination":
          case "Sub Source Combination":
            if (
              payload.name === originalData.name &&
              payload.subSourceNumber === originalData.subSourceNumber
            ) {
              toast.error("No changes detected. Please update at least one field before submitting.");
              setIsModalLoading(false);
              return;
            }
            break;

          case "TPUsers":
          case "TpUsers":
            if (
              payload.name === originalData.name &&
              payload.email === originalData.email &&
              payload.roleId === originalData.roleId
              // password comparison skipped for security (usually hashed or not returned)
            ) {
              toast.error("No changes detected. Please update at least one field before submitting.");
              setIsModalLoading(false);
              return;
            }
            break;

          case "TpLeads":
          case "Leads":
            const leadsFromPayload = JSON.parse(payload.leads);
            const originalLeads = originalData.leads || [];

            const allLeadsSame = leadsFromPayload.every((lead, index) => {
              const original = originalLeads[index] || {};
              return (
                lead.name === original.name &&
                lead.mobile === original.mobile &&
                lead.pincode === original.pincode &&
                JSON.stringify(lead.services) === JSON.stringify(original.services)
              );
            });

            if (allLeadsSame) {
              toast.error("No changes detected. Please update the lead(s) before submitting.");
              setIsModalLoading(false);
              return;
            }
            break;

          default:
            break;
        }
      }


      if (pageType === "TpLeads") {
        const formData = new FormData();
        const payload = getPayload(data);
        Object.keys(payload).forEach((key) => {
          formData.append(key, payload[key]);
        });
        apiCall = createCombination(formData);
      } else {
        apiCall = isEdit ? editCombination(editId, payload) : createCombination(payload);
      }

      await apiCall.then((res) => {
        setTimeout(async () => {
          setIsModalLoading(false);
          toast.success(res?.data?.message);
          await getAllLeads(apiParams);
          setIsCombinationModalOpen(false);
          setIsEdit(false);
          setEditId(null);
          reset();
        }, 500);
      }).catch((err) => {
        reset();
        setTimeout(() => {
          setIsCombinationModalOpen(false);
          setIsModalLoading(false);
          setIsEdit(false);
          setEditId(null);
          toast.error(err?.response?.data?.message || err.message);
        }, 1000);
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong");
    }
  }

  // Edit Combination
  const handleEdit = async (id) => {
    setIsModalLoading(true);
    try {
      let apiCall = pageType === 'TPUsers' ? getSubAdminDetailsById : getSpecificCombination;
      const res = await apiCall(id); // API Call
      if (res) {
        const result = res?.data?.data || res?.data?.result
        setEditId(id);
        setIsEdit(true);
        const formattedData = {
          ...result,
          roleId: result?.roleId?._id || "",
          companyId: result?.companyId || "",
          source: String(result?.source?.value) || "",
          subSource: String(result?.subSource?.value) || "",
        };
        setOriginalData(formattedData);
        reset(formattedData);
        setSelectedRole([result?.roleId?._id])
        setIsCombinationModalOpen(true);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setIsModalLoading(false);
    }
  };

  // Delete Combination
  const handleDelete = async (id) => {
    setIsDeleteModalLoading(true);
    try {
      let apiCall = pageType === 'TpUsers' ? getSubAdminDetailsById : getSpecificCombination;
      const res = await apiCall(id);
      if (res) {
        const result = res?.data?.data || res?.data?.result
        setDeleteId(id);
        setIsDeleted(true);
        setDeleteData(result?.brand || result?.name);
        setIsDeleteCombinationModalOpen(true);
      }
    } catch (error) {
      setDeleteId(null)
      setDeleteData({})
      setIsDeleted(false);
      setIsDeleteModalLoading(false);
      toast.error("Failed to Delete record!");
    } finally {
      setIsDeleted(false);
      setIsDeleteModalLoading(false);
    }
  }

  const handleConfirmDeleteCombination = async () => {
    setIsDeleteModalLoading(true);
    try {
      await deleteCombination(deleteId).then(async (res) => {
        setTimeout(() => {
          setDeleteData({})
          setIsDeleted(false);
          setIsDeleteModalLoading(false);
          setDeleteId(null)
          toast.success(res?.data?.message);
          setIsDeleteCombinationModalOpen(false);
        }, 500);
        await getAllLeads(apiParams);
      }).catch((err) => {
        setDeleteData({})
        setIsDeleted(false);
        setIsDeleteModalLoading(false);
        setDeleteId(null)
        toast.error(err?.response?.data?.message || err.message);
        setIsDeleteCombinationModalOpen(false);
      })
    } catch (error) {
      setIsDeleteModalLoading(false);
      console.error("Error:", error);
      toast.error(error?.message || "Something went wrong");
    }
  }


  const isAppliedSearchUnchanged = searchApplied && searchInput.trim() === appliedSearch;

  return (
    <div className="inline-flex overflow-hidden max-w-full w-full h-full rounded-global shadow-global">
      <div className="lead-view-wrapper bg-white rounded-global shadow-global transition-width duration-300 h-full flex-[100] max-w-full w-full">
        <div className="grid-layout h-full">
          <div className="w-full lead-view-header flex flex-wrap items-center justify-between border-b border-default-200 py-4 px-6">

            <div className="flex items-center gap-5 flex-row-reverse">
              {isSearchable && (
                <>
                  <div className="flex items-center gap-2 border border-default-200 rounded-[13px] hover:bg-gray-100">
                    <Input
                      classNames={{
                        base: "max-w-full sm:max-w-[350px] h-10",
                        inputWrapper: "h-full font-normal text-default-500 bg-transparent  rounded-[13px]",
                      }}
                      onChange={handleSearchInputChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          searchFunctions();
                        }
                      }}
                      value={searchInput}
                      placeholder="Type to search..."
                      size="sm"
                      startContent={<SearchIcon size={18} />}
                      type="text"
                    />
                    <Button
                      onPress={isAppliedSearchUnchanged ? clearSearch : searchFunctions}
                      size="sm"
                      className="h-6 min-w-10 p-0  mr-2 bg-success text-white rounded-[13px]"
                    >
                      {isAppliedSearchUnchanged ? <IoClose /> : <FaArrowRight />}
                    </Button>
                  </div>
                  <div className="flex items-center text-gray-300">|</div>
                </>
              )}
              <div className="total-leads-documents flex items-center whitespace-nowrap gap-1">
                <span className="text-medium text-success">Total {changedTitle} :</span>
                <span className="text-large font-bold">{formatNumberValue(allLeadsData?.totalDocs) || 'N/A'}</span>
              </div>
            </div>
            <div className="leads-data-filter flex items-center gap-6">
              <DynamicPopover
                leadType={pageType}
                isLoading={columnLoading}
                buttonText="Columns"
                items={columnsData}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                toggleItem={toggleItem}
                placeholder="Search columns..."
                popoverTitle="Select Columns"
                savePreference={savePreference} fetchPreferences={fetchPreferences} deletePreference={deletePreference}
              />

              {(isleadAction && isleadAction.create) && (
                <Button
                  onPress={toggleCombinationModal}
                  className={`hover:bg-button font-mediem text-medium hover:border-secondary border-1 border-gray-200 text-gray-500 hover:text-white transition-colors duration-500 ${isCombinationModalOpen ? "bg-button text-white border-secondary" : "hover:bg-button hover:border-secondary hover:text-white"
                    }`}
                  variant="bordered"
                  startContent={changedTitle === 'Brand Combination' ? <BrandCombinationIcon /> : changedTitle === 'Source Combination' ? <SourceIcon /> : changedTitle === 'Sub Source Combination' ? <SubSourceIcon /> : <CreateIcon width="30px" height="30px" />}
                >
                  Create {changedTitle}
                </Button>
              )}

              {isCombinationModalOpen && (
                <CreateCombinations
                  title={`${isEdit ? "Edit" : "Create"} ${changedTitle}`}
                  isOpen={isCombinationModalOpen}
                  onOpen={() => setIsCombinationModalOpen(true)}
                  onClose={() => setIsCombinationModalOpen(false)}
                  isEdit={true}
                >
                  <ModalBody>
                    <div className="pb-4 w-full max-h-[600px] overflow-y-auto default-scrollbar">

                      {(pageType === 'BrandCombination' || pageType === "Brand Combination") && (
                        <div className={`${pageType} flex flex-wrap gap-4 justify-between w-full`}>
                          <Select
                            {...register("source")}
                            isInvalid={!!errors.source}
                            errorMessage={errors.source && errors.source.message}
                            isRequired
                            className="max-w-[48%]"
                            label="Select Source"
                            selectedKeys={new Set(watch("source") ? [watch("source")] : [])}
                            placeholder="Select Source"
                          >
                            {selectSource.map((source) => (
                              <SelectItem key={source.value} value={source.value}>
                                {source.label}
                              </SelectItem>
                            ))}
                          </Select>
                          <Select
                            {...register("subSource")}
                            isInvalid={errors.subSource ? true : false}
                            errorMessage={errors.subSource && errors.subSource.message}
                            isRequired
                            className="max-w-[48%]"
                            label="Select Sub Source"
                            placeholder="Select Sub Source"
                            selectedKeys={new Set(watch("subSource") ? [watch("subSource")] : [])}
                          >
                            {selectSubSource.map((source) => (
                              <SelectItem key={source.value} value={source.value}>
                                {source.label}
                              </SelectItem>
                            ))}
                          </Select>
                          <Input label="Brand" {...register("brand")} name="brand" placeholder="Enter Brand Name" type="text"
                            isInvalid={errors.brand ? true : false}
                            errorMessage={errors.brand && errors.brand.message}
                          />
                        </div>
                      )}

                      {(pageType === 'SourceCombination' || pageType === "Source Combination") && (
                        <div className={`${pageType} flex flex-wrap gap-4 justify-between w-full`}>
                          <Input {...register("name")} label="Enter Source Name" name="name" type="text"
                            isInvalid={errors.name ? true : false}
                            errorMessage={errors.name && errors.name.message}
                          />
                          <Input {...register("sourceNumber")} label="Enter Source Number" name="sourceNumber" type="number"
                            isInvalid={errors.sourceNumber ? true : false}
                            errorMessage={errors.sourceNumber && errors.sourceNumber.message}
                          />
                        </div>
                      )}

                      {(pageType === 'SubSourceCombination' || pageType === "Sub Source Combination") && (
                        <div className={`${pageType} flex flex-wrap gap-4 justify-between w-full`}>
                          <Input {...register("name")} label="Enter Sub Source Name" name="name" type="text"
                            isInvalid={errors.name ? true : false}
                            errorMessage={errors.name && errors.name.message}
                          />
                          <Input {...register("subSourceNumber")} label="Enter Sub Source Number" name="subSourceNumber" type="number"
                            isInvalid={errors.subSourceNumber ? true : false}
                            errorMessage={errors.subSourceNumber && errors.subSourceNumber.message}
                          />
                        </div>
                      )}

                      {(pageType === 'TpLeads' || pageType === 'Leads') && (
                        <div className="flex flex-col gap-4  overflow-y-auto ">
                          {fields.map((item, index) => (
                            <div className="flex gap-8 border p-6 rounded-lg border-gray-100 relative">
                              <div className="absoultue-styeleCST bg-white text-black   border-b border-r border-gray-100 rounded-[7px_0px] absolute top-0 left-0 w-[20px] h-[20px] flex items-center text-[10px] pl-[6px] ">{index + 1}</div>
                              <div key={item.id} className={`${pageType} flex flex-wrap gap-4 justify-start w-full`}>
                                <Input
                                  {...register(`leads.${index}.name`)}
                                  className="max-w-[48%]"
                                  label="Enter Name"
                                  type="text"
                                  isInvalid={errors?.leads?.[index]?.name ? true : false}
                                  errorMessage={errors?.leads?.[index]?.name?.message}
                                />
                                <Input
                                  {...register(`leads.${index}.mobile`)}
                                  className="max-w-[48%]"
                                  label="Enter Mobile Number"
                                  type="number"
                                  isInvalid={errors?.leads?.[index]?.mobile ? true : false}
                                  errorMessage={errors?.leads?.[index]?.mobile?.message}
                                />
                                <Input
                                  {...register(`leads.${index}.pincode`)}
                                  className="max-w-[48%]"
                                  label="Enter Pincode"
                                  type="number"
                                  isInvalid={errors?.leads?.[index]?.pincode ? true : false}
                                  errorMessage={errors?.leads?.[index]?.pincode?.message}
                                />
                                <div className="flex flex-col max-w-[42%] gap-1">
                                  <div className="flex justify-between">
                                    <Checkbox
                                      {...register(`leads.${index}.services.OLA`)}
                                      classNames={{ icon: "text-white" }}
                                      color="success"
                                      className="max-w-[15%]"
                                      defaultSelected
                                      isInvalid={errors?.leads?.[index]?.services ? true : false}
                                      errorMessage={errors?.leads?.[index]?.services?.message}
                                    >
                                      Ola
                                    </Checkbox>
                                    <Checkbox
                                      {...register(`leads.${index}.services.TVS`)}
                                      classNames={{ icon: "text-white" }}
                                      color="success"
                                      className="max-w-[15%]"
                                      isInvalid={errors?.leads?.[index]?.services ? true : false}
                                      errorMessage={errors?.leads?.[index]?.services?.message}
                                    >
                                      Tvs
                                    </Checkbox>
                                  </div>
                                  <div className="flex justify-between">
                                    <Checkbox
                                      {...register(`leads.${index}.services.AMPERE`)}
                                      classNames={{ icon: "text-white" }}
                                      color="success"
                                      className="max-w-[15%]"
                                      isInvalid={errors?.leads?.[index]?.services ? true : false}
                                      errorMessage={errors?.leads?.[index]?.services?.message}
                                    >
                                      Ampere
                                    </Checkbox>
                                    <Checkbox
                                      {...register(`leads.${index}.services.REVOLT`)}
                                      classNames={{ icon: "text-white" }}
                                      color="success"
                                      className="max-w-[15%]"
                                      isInvalid={errors?.leads?.[index]?.services ? true : false}
                                      errorMessage={errors?.leads?.[index]?.services?.message}
                                    >
                                      Revolt
                                    </Checkbox>
                                  </div>
                                  <div className="flex justify-between">
                                    <Checkbox
                                      {...register(`leads.${index}.services.SIMPLE`)}
                                      classNames={{ icon: "text-white" }}
                                      color="success"
                                      className="max-w-[15%]"
                                      isInvalid={errors?.leads?.[index]?.services ? true : false}
                                      errorMessage={errors?.leads?.[index]?.services?.message}
                                    >
                                      Simple
                                    </Checkbox>
                                  </div>
                                </div>
                              </div>
                              <hr className="h-auto border border-gray-100" />
                              <div className="flex justify-end mt-4">
                                {index === fields.length - 1 && fields.length < 5 ? (
                                  <CreateIcon className="cursor-pointer hover:text-success" onClick={() => append({ name: "", mobile: "", pincode: "" })} />
                                ) : (
                                  <CloseIcon className="cursor-pointer hover:text-red-700" onClick={() => remove(index)} />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(pageType === 'TpUsers' || pageType === 'TPUsers') && (
                        <div className={`${pageType} flex flex-wrap gap-4 justify-between w-full`}>
                          <Input {...register("name")} className="max-w-[48.5%]" label="Enter Name" name="name" type="text"
                            isInvalid={errors.name ? true : false}
                            errorMessage={errors.name && errors.name.message}
                          />
                          <Input {...register("email")} className="max-w-[48.5%]" label="Enter Email" name="email" type="text"
                            isInvalid={errors.email ? true : false}
                            errorMessage={errors.email && errors.email.message}
                          />
                          <Input {...register("password")} className="max-w-[48.5%]" label="Enter Password" name="password" type="text"
                            isInvalid={errors.password ? true : false}
                            errorMessage={errors.password && errors.password.message}
                          />
                          <Select
                            {...register("roleId")}
                            isInvalid={!!errors.roleId}
                            errorMessage={errors.roleId && errors.roleId.message}
                            isRequired
                            className="max-w-[48.5%]"
                            label="Select Role"
                            selectedKeys={new Set(watch("roleId") ? [watch("roleId")] : [])}
                            placeholder="Select Role"
                            onSelectionChange={(selected) => {
                              const selectedArray = Array.from(selected).map((item) => item.toString());
                              setSelectedRole(selectedArray);
                            }}

                          >
                            {roleOption.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </Select>
                          {selectedRole[0] === '6610f0fbd22a12c26454baf4' && (
                            <>
                              {isCompanyoptionLoaded ? (
                                <Skeleton className="w-full h-[56px] p-2 px-4 rounded-[12px]" />
                              ) : (
                                <Select
                                  {...register("companyId", { required: "Company is required" })}
                                  isInvalid={!!errors.companyId}
                                  errorMessage={errors.companyId && errors.companyId.message}
                                  isRequired
                                  selectedKeys={new Set(watch("companyId") ? [watch("companyId")] : [])}
                                  label="Select Company"
                                  placeholder="Select Company"
                                >
                                  {companyoption.map((company) => (
                                    <SelectItem key={company.value} value={company.value}>
                                      {company.label}
                                    </SelectItem>
                                  ))}
                                </Select>
                              )}
                            </>
                          )}

                        </div>
                      )}
                    </div>
                  </ModalBody>
                  <ModalFooter className="border-t border-gray-100">
                    <Button color="primary" variant="flat" onPress={() => {
                      setIsCombinationModalOpen(false)
                      reset();
                    }}>
                      <p className="text-primary-900">Cancel</p>
                    </Button>
                    <Button color="secondary" onPress={handleSubmit(handlehandleConfirm)}>
                      {isModalLoading || isDeleteModalLoading ? (
                        <Spinner color="success" size="sm" />
                      ) : (
                        <p className="text-black">{isEdit ? "Update" : "Create"}</p>
                      )}
                    </Button>
                  </ModalFooter>

                </CreateCombinations>
              )}

              {isDeleteCombinationModalOpen && (
                <ModalBox
                  title={`Confirm Delete`}
                  isOpen={isDeleteCombinationModalOpen}
                  onOpen={() => setIsDeleteCombinationModalOpen(true)}
                  onClose={() => setIsDeleteCombinationModalOpen(false)}
                  handleConfirmUpdate={handleConfirmDeleteCombination}
                  isDelete={true}
                  isLoading={isDeleteModalLoading}
                >
                  <div className="flex flex-col">
                    <p>Do you really want to delete this <span className="text-amber-600 font-semibold">{deleteData}</span> {changedTitle}?</p>
                    <p>This process cannot be undone.</p>
                  </div>
                </ModalBox>
              )}

            </div>
          </div>

          <div className="leadsData overflow-x-auto h-[calc(100%-72px)] flex items-center justify-center">
            {(loading || isLoading) ? (
              <div className="w-full flex items-center justify-center gap-2">
                <Spinner color="success" />
                <p className="!text-success text-3xl">Loading...</p>
              </div>
            ) : <TableData user={user} handleEdit={handleEdit} handleDelete={handleDelete} debouncedSearch={debouncedSearch} allLeadsData={allLeadsData} isleadAction={isleadAction} columns={columnsData} selectedColumns={selectedColumns} setPageLimit={setPageLimit} pageLimit={pageLimit} getAllLeads={getAllLeads} leadType={pageType} />}

          </div>
        </div>
      </div>
      <Toaster
        position="top-right"
        reverseOrder={false}
      />
    </div>
  );
}